import React, {useState,useRef,useContext,useEffect} from "react";
import { useTranslate } from 'react-redux-multilingual';
import { MatchCsv,CardPerso,BtnImportModel } from "@gull";
import { CSVReader } from 'react-papaparse';
import axios from 'axios'
import swal from "sweetalert2";
import { IsAuthContext } from 'app/App';

/**
 * Import CSV des prix personnalisés (matching de colonnes puis envoi au backend).
 *
 * @param {number|string} [clientId] - ID du client déjà connu (fiche client). Si fourni, le champ
 *   de matching "client" n'est pas proposé et cet ID est envoyé tel quel pour tout l'import.
 */
const ImportPersonalizedPrice = (props) => {
    const t = useTranslate()
    const [errorCsv,setErrorCsv] = useState(false)
    const [csvHeader,setCsvHeader] = useState([])
    const [csvData,setCsvData] = useState([])
    const [preSendError,setPreSendError] = useState(false)
    const [showValidButton,setShowValidButton] = useState(false)
    const selectMatch = useRef(new Array())
    const [fileName,setFileName] = useState('');
    const [validButton,setValidButton] = useState(false)
    const [apiError,setApiError] = useState(false)
    const [resetCsv,setResetCsv] = useState(false)
    const {authParam} = useContext(IsAuthContext);
    const [isForUpdate,setIsForUpdate] = useState(false);

    // Champ utilisé pour identifier l'article ciblé par le prix personnalisé (voir CsvParameters.jsx pour la config company)
    const matchingFieldsConfig = authParam.company.advanceparams?.csv_matching_fields
        ?? { internalid: true, sku: false, reference: false, name: false }
    const [matchingField,setMatchingField] = useState('internalid')
    let matchingFieldLabels = {
        internalid: t('internalId'),
        sku: t('sku'),
        reference: t('reference'),
        name: t('itemName'),
    }
    let matchingFieldOptions = ['internalid','sku','reference','name'].filter((field) => matchingFieldsConfig[field])

    // Quand on change la donnée d'identification après le dépôt du CSV, on remet à jour le select de la
    // colonne d'identification en ré-auto-détectant l'en-tête correspondant (ou "-- Select --" si aucun),
    // via un vrai événement 'change' pour repasser par la validation existante (sans remonter le composant,
    // ce qui casserait la ref utilisée par MatchCsv)
    useEffect(() => {
        if(csvHeader.length===0) return
        const identIndex = optionMatchCsv.length-1
        const identSelect = selectMatch.current[identIndex]
        if(!identSelect) return
        const matchedIndex = (csvHeader.includes(matchingFieldLabels[matchingField])) ? String(csvHeader.indexOf(matchingFieldLabels[matchingField])) : 'no'
        if(identSelect.value!==matchedIndex){
            // Passe par le setter natif (et non celui instrumenté par React) pour que le dispatch
            // du 'change' ci-dessous soit bien détecté par React et déclenche onChange sur le select
            const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype,'value').set
            nativeValueSetter.call(identSelect, matchedIndex)
            identSelect.dispatchEvent(new Event('change', { bubbles: true }))
        }
    }, [matchingField])

    // Champ d'identification de l'article : placé en dernière position (comme dans le comportement d'origine),
    // son libellé et sa clé de mapping suivent le champ de matching sélectionné (internalid/sku/reference/name)
    // Le matching de la colonne "client" n'est proposé que si l'ID client n'est pas déjà connu (props.clientId),
    // ce qui est le cas quand l'import est lancé depuis la fiche d'un client
    let option = [
        {name:'type', trad:'Type'+'*'},
        {name:'value', trad:t('value')+'*'},
        {name:'begin', trad:t('beginPersonalizedPrice')},
        {name:'end', trad:t('endPersonalizedPrice')},
        ...(props.clientId ? [] : [{name:'client_id', trad:t('customer')}]),
        // Obligatoire : un prix personnalisé sans société associée est supprimé automatiquement par le job d'import
        // (ImportPersonalizedPriceArticle) qui nettoie les prix orphelins sans company_selection/other_company
        {name:'company', trad:t('company')+'*'},
        {name:matchingField, trad:matchingFieldLabels[matchingField]},
    ]

    let optionMatchCsvModelDownload = {}

    let optionMatchCsv = []

    let convert = {}

    option.map((value)=>{
        optionMatchCsv.push(value.trad)
        convert[value.trad]=value.name
        optionMatchCsvModelDownload[value.trad] = ''
    })

    const handleOnDrop = (data,file) => {
        setApiError(false)
        setErrorCsv(false)
        setPreSendError(false)
        setShowValidButton(false)
        setResetCsv(false)
        setFileName(file.name)

        //On calcule le poids du fichier en mégaoctets
        const fileSizeInMegabytes = file.size / (1024 * 1024);
        
        if (fileSizeInMegabytes <= 7) {
            if(data.length>process.env.REACT_APP_CSV_IMPORT_LIMIT){
                setErrorCsv(t('limitCsv',{limit: process.env.REACT_APP_CSV_IMPORT_LIMIT}))

            }else if(data[0].data.length!=0){
                setCsvHeader(data[0].data)
                if(data[data.length-1].data.length==1){
                    data.pop()
                }
                setCsvData(data)
                let verifDoublon = new Array()
                let valid=true
                selectMatch.current.forEach((r)=>{
                    if(r.value!='no'){
                        if(!verifDoublon.includes(r.value)){
                            verifDoublon.push(r.value)
                        }
                        else{
                            valid=false
                        }
                    }
                })
                if(!valid){
                    setPreSendError(t('doublonCsv'))
                }
                else{
                    let valid=true
                    optionMatchCsv.forEach((v,index) =>{
                        if(v.match(/\*$/)){
                            if(selectMatch.current[index].value=="no") valid=false
                        }
                    })

                    if(valid){
                        setShowValidButton(true)
                    }
                }
            }else{ 
                setErrorCsv(t('noDataInCsv'))
            }
        }else{
            setErrorCsv(t('fileCsvToLarge'))
        }
    };

    const handleOnError = (err, file, inputElem, reason) => {
        setErrorCsv(t('unknowError'))
        setCsvHeader([])
        setFileName('')
    };
    
    const handleOnRemoveFile = (data) => {
        setApiError(false)
        setErrorCsv(false)
        setCsvHeader([])
        setPreSendError(false)
        setFileName('')
        selectMatch.current = []
    };

    const goImport = async () => {
        setValidButton(true)
        setApiError(false)
        
        let matchColonne = {}
        optionMatchCsv.forEach((optm,index)=>{
            matchColonne[convert[optm]]=(selectMatch.current[index].value!="no") ? selectMatch.current[index].value : null
        })

        const response  = await axios.post(process.env.REACT_APP_API_URL+"api/historique",{
            name: 'personalizedPriceImport',
            totalLines: csvData.length-1,
            filename: fileName
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'<br>')
                messageError=messageError.replace('undefined','');
                setApiError(messageError)
            }
            else{
                axios.post(process.env.REACT_APP_API_URL+"api/erp/personalized_price/csvimport_personalized_price/"+response.data.data.id,{
                    match: matchColonne,
                    data: csvData,
                    matching_field: matchingField,
                    client_id: props.clientId,
                }).then((response) => {
                }).catch((error) => {
                    if(error=='end_session') return;
                })
                setCsvHeader([])
                setResetCsv(true)
                return(
                    swal.fire({
                        icon: 'success',
                        title: t('successInHistorique')
                    })
                ) 
            }
        }).catch((error) => {
            if(error=='end_session') return;
            if(error.response==undefined){
                setApiError(t('unknowError'))
            }
            else if(error.response.status==403){
                setApiError(t('forbiddenGeneral'))
            }
            else {
                setApiError(t('unknowError'))
            }
        })
        setValidButton(false)
    }

    return (
        <div className="row">
            <div className="col-12">
                <CardPerso header={t('import')}>
                    <div className="text-end mb-1">
                        <BtnImportModel
                            modele={//Passer false si aucun modle à télécharger
                                {
                                    fileName:t('template')+' '+t('importPersonalizedPrice'),
                                    data:[
                                        optionMatchCsvModelDownload
                                    ]
                                }
                            }
                        />
                    </div>
                    <CSVReader
                        onDrop={handleOnDrop}
                        onError={handleOnError}
                        addRemoveButton
                        onRemoveFile={handleOnRemoveFile}
                        isReset={resetCsv}
                        config={{
                            delimiter:authParam.company.advanceparams.csv_separator,
                            encoding:authParam.company.advanceparams.csv_encode
                        }}
                    >
                        <span>{t('dropCsv')}</span>
                    </CSVReader>
                    {(csvHeader.length!=0) &&
                        <div className="col-12 mt-3">
                            {(matchingFieldOptions.length>1) &&
                                <div className="row justify-content-center">
                                    <div className="form-group col-xl-4 col-lg-6 col-8 mb-3 text-center">
                                        <label htmlFor="matching_field">{t('labelArticleIdentificationField')}</label>
                                        <select
                                            id="matching_field"
                                            className="form-control form-control-sm"
                                            value={matchingField}
                                            onChange={(e) => setMatchingField(e.target.value)}
                                        >
                                            {matchingFieldOptions.map((field) =>
                                                <option key={field} value={field}>{matchingFieldLabels[field]}</option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            }
                            <big>
                                {optionMatchCsv.map((value,index) =>
                                    <MatchCsv  
                                        key={index}
                                        index={index}
                                        optionMatchCsv={optionMatchCsv} 
                                        toMatch={value} 
                                        csvHeader={csvHeader} 
                                        ref={selectMatch} 
                                        preSendError={preSendError} 
                                        setPreSendError={setPreSendError} 
                                        showValidButton={showValidButton} 
                                        setShowValidButton={setShowValidButton}
                                        updateField= '0'
                                        isForUpdate={isForUpdate}
                                        setIsForUpdate={setIsForUpdate}
                                    />
                                )}
                            </big>
                            {(preSendError) &&
                                <div className="alert alert-danger text-center">{preSendError}</div>
                            }
                            {(apiError) ? <div className="alert alert-danger mt-3 text-center" dangerouslySetInnerHTML={{ __html: apiError }}></div> : false}
                            {(showValidButton) &&
                                <div className="col-12 text-center">
                                    <button disabled={validButton} className="btn btn-primary" onClick={() => goImport()}>
                                        {validButton ? <span><span>&nbsp;</span><div className="spinner-border spinner-border-sm"></div></span> : t('btnInsert')}
                                    </button>
                                </div>
                            }
                        </div>
                    }
                    {(errorCsv) &&
                        <div className="alert alert-danger text-center">{errorCsv}</div>
                    }   
                </CardPerso>
            </div>
        </div>
    );
}

export default ImportPersonalizedPrice;
